import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { trackAddToCart } from '../lib/gads';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('allmx_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    // Save cart to localStorage whenever it changes
    localStorage.setItem('allmx_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.sku === product.sku);
      
      if (existingItem) {
        // Update quantity if product already in cart
        return prevCart.map(item =>
          item.sku === product.sku
            ? { ...item, quantity: Math.min(item.quantity + quantity, product.stock) }
            : item
        );
      } else {
        // Add new product to cart
        return [...prevCart, {
          sku: product.sku,
          name_it: product.name_it,
          name_en: product.name_en,
          price: product.price,
          image: product.images && product.images.length > 0 ? product.images[0] : null,
          quantity: Math.min(quantity, product.stock),
          maxStock: product.stock
        }];
      }
    });
    
    // Google Ads remarketing: add_to_cart event
    trackAddToCart(product, quantity);

    toast.success('Prodotto aggiunto al carrello!');
  };

  const removeFromCart = (sku) => {
    setCart(prevCart => prevCart.filter(item => item.sku !== sku));
    toast.success('Prodotto rimosso dal carrello');
  };

  const updateQuantity = (sku, quantity) => {
    if (quantity <= 0) {
      removeFromCart(sku);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.sku === sku
          ? { ...item, quantity: Math.min(quantity, item.maxStock) }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    toast.success('Carrello svuotato');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
